import React, { createContext, FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSysAttributes } from "../../../hooks/attributes/useSysAttributes";
import { FileURLInfo } from "../DialogComponents/FileUploader/types";
import { LayerAttribute } from "../EditResource/components/Layer/LayerAttributeItem";

import { MAX_UPLOAD_STEPS } from "./constants";
import { useUploadPolling } from "./hooks/useUploadPolling";
import { useDialogInfo } from "../../../hooks/useDialogs";
import { useLayerAttributes } from "../../../hooks/layer/usePostgresLayer";
import { UploadDataContextType, UploadFileAttributes, UploadFileStep } from "./types";

const UploadDataContext = createContext<UploadDataContextType>({ step: UploadFileStep.Files });

const uploadAttributeNamesToObject = (names: string[]): UploadFileAttributes => {
  return names.reduce((prev, name) => ({ ...prev, [name]: "" }), {});
};

// eslint-disable-next-line max-lines-per-function
export const UploadDataProvider: FC<{ data: UploadDataContextType }> = ({ data, children }) => {
  const { step: stepProp, nextText } = data;
  const { resources } = useDialogInfo();
  const attributes = useLayerAttributes(resources[0], true);
  const [step, setStep] = useState(stepProp);
  const [files, setFiles] = useState<Array<File | FileURLInfo>>([]);
  const [dateOfImage, setDateOfImage] = useState<Date>();
  const [uploadAttributes, setUploadAttributes] = useState<UploadFileAttributes>({});
  const [sysAttributes, { addSysAttributes, deleteSysAttribute, updateSysAttribute }] =
    useSysAttributes<LayerAttribute>();
  const [maxZoomLevel, setMaxZoomLevel] = useState<number | undefined>();

  const isAllSelected = Object.keys(attributes).length === Object.keys(uploadAttributes).length;

  const nextStep = useCallback(() => {
    setStep(step < MAX_UPLOAD_STEPS ? step + 1 : step);
  }, [step]);

  const prevStep = useCallback(() => {
    setStep(step > 0 ? step - 1 : step);
  }, [step]);

  const prevFiles = useRef(files);

  useEffect(() => {
    prevFiles.current = [...files];
  }, [files]);

  const uploadFiles = useCallback((uploadingFiles: File[]) => {
    setFiles([
      ...prevFiles.current,
      ...uploadingFiles.filter(item => !prevFiles.current.some(fileItem => (fileItem as File).name === item.name)),
    ]);
    setDateOfImage(new Date());
  }, []);

  const uploadFileURL = useCallback((fileInfo: FileURLInfo) => {
    if (prevFiles.current.find(item => (item as FileURLInfo).fileName === fileInfo.fileName)) {
      return;
    }

    setFiles([...prevFiles.current, fileInfo]);
    setDateOfImage(new Date());
  }, []);

  const onFileDelete = useCallback(
    (name: string) => {
      setFiles(files.filter(item => ((item as File).name || (item as FileURLInfo).fileName) !== name));
    },
    [files],
  );

  const onChangeAttributeCheckbox = useCallback(
    name => {
      if (Object.prototype.hasOwnProperty.call(uploadAttributes, name)) {
        delete uploadAttributes[name];
        setUploadAttributes({ ...uploadAttributes });
      } else {
        setUploadAttributes({ ...uploadAttributes, [name]: "" });
      }
    },
    [uploadAttributes],
  );

  const onChangeAttributeValue = useCallback(
    (attributeName, attributeValue) => {
      setUploadAttributes({
        ...uploadAttributes,
        [attributeName]: attributeValue,
      });
    },
    [uploadAttributes],
  );

  const onSelectAllAttributes = useCallback(() => {
    setUploadAttributes(
      isAllSelected
        ? {}
        : {
            ...uploadAttributes,
            ...uploadAttributeNamesToObject(
              Object.keys(attributes).filter(name => !Object.prototype.hasOwnProperty.call(uploadAttributes, name)),
            ),
          },
    );
  }, [attributes, isAllSelected, uploadAttributes]);

  const { uploadDone, uploadedFiles, uploadError, uploadDelete } = useUploadPolling(files, onFileDelete);

  useEffect(() => {
    setUploadAttributes(uploadAttributeNamesToObject(Object.keys(attributes)));
  }, [attributes]);

  const value: UploadDataContextType = {
    step,
    nextText,
    nextStep,
    prevStep,
    uploadFiles,
    uploadDone,
    uploadedFiles,
    uploadFileURL,
    uploadError,
    uploadDelete,
    uploadAttributes,
    dateOfImage,
    maxZoomLevel,
    setMaxZoomLevel,
    onSelectAllAttributes,
    onChangeAttributeCheckbox,
    onChangeAttributeValue,
    sysAttributes,
    addSysAttributes,
    deleteSysAttribute,
    updateSysAttribute,
  };

  return <UploadDataContext.Provider value={value}>{children}</UploadDataContext.Provider>;
};

export const useUploadDataContext = (): UploadDataContextType => {
  return useContext(UploadDataContext);
};
